import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useToast, errorText } from '@despachador/ui';
import { api, socket } from './lib/api';
import { audioUnlocked, keepAwake, startAlert, stopAlert, unlockAudio } from './lib/alerts';
import { clearOfferPushes, enablePush, isNativeApp, radioAudio, startLocation, stopLocation, syncPush, type GeoState } from './lib/device';
import { VoiceCall } from './lib/voice';
import type { RadioClient } from '@despachador/api-client/ptt';
import type { ActiveService, DriverMe, NotificationSettings, TripResult } from './types';

export interface Offer {
  offerId: string;
  serviceId: string;
  code: string;
  originText: string | null;
  destinationText: string | null;
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  fareText: string | null;
  etaS: number | null;
  distanceM: number | null;
  /** the trip itself (pickup -> destination); null while the destination has no point */
  tripDistanceM: number | null;
  expiresAt: string;
  timeoutS: number;
}

export interface IncomingCall {
  sessionId: string;
  from: { name: string };
  call: VoiceCall;
  state: 'ringing' | 'active';
}

interface DriverState {
  me: DriverMe | null;
  loading: boolean;
  /** Why the profile could not be loaded (only while `me` is still null). */
  loadError: string | null;
  presence: string;
  socketState: string;
  offer: Offer | null;
  active: ActiveService | null;
  /** How the last trip ended, until the driver dismisses it or a new one starts. */
  result: TripResult | null;
  dismissResult: () => void;
  geo: GeoState;
  audioReady: boolean;
  alertFallback: boolean;
  call: IncomingCall | null;
  /** Push-to-talk radio (null when the tenant has not enabled it). Its own store: see useRadio(). */
  radio: RadioClient | null;
  refresh: () => Promise<void>;
  setPresence: (s: 'ONLINE' | 'PAUSED' | 'OFFLINE') => Promise<void>;
  respond: (accept: boolean) => Promise<void>;
  action: (a: string, reason?: string) => Promise<boolean>;
  enableAudio: () => Promise<void>;
  saveSettings: (s: Record<string, unknown>) => Promise<void>;
  acceptCall: () => Promise<void>;
  endCall: (reject: boolean) => Promise<void>;
}

const Ctx = createContext<DriverState | null>(null);
export const useDriver = () => useContext(Ctx)!;

function toOffer(o: any): Offer {
  return {
    offerId: o.offerId ?? o.offer_id,
    serviceId: o.serviceId ?? o.service_id,
    code: o.code,
    originText: o.originText ?? o.origin_text,
    destinationText: o.destinationText ?? o.destination_text,
    origin: o.origin ?? { lat: o.origin_lat, lng: o.origin_lng },
    destination: o.destination ?? { lat: o.destination_lat, lng: o.destination_lng },
    fareText: o.fareText ?? null,
    etaS: o.etaS ?? o.eta_s ?? null,
    distanceM: o.distanceM ?? o.distance_m ?? null,
    tripDistanceM: o.tripDistanceM ?? o.trip_distance_m ?? null,
    expiresAt: o.expiresAt ?? o.expires_at,
    timeoutS: o.timeoutS ?? Math.max(1, Math.round((new Date(o.expires_at).getTime() - Date.now()) / 1000)),
  };
}

export function DriverProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [me, setMe] = useState<DriverMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [presence, setPresenceState] = useState('OFFLINE');
  const [socketState, setSocketState] = useState(socket.state);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [active, setActive] = useState<ActiveService | null>(null);
  const [result, setResult] = useState<TripResult | null>(null);
  const activeRef = useRef<ActiveService | null>(null);
  activeRef.current = active;
  const [geo, setGeo] = useState<GeoState>('off');
  const [audioReady, setAudioReady] = useState(audioUnlocked());
  const [alertFallback, setAlertFallback] = useState(false);
  const [call, setCall] = useState<IncomingCall | null>(null);
  const [radio, setRadio] = useState<RadioClient | null>(null);
  const settingsRef = useRef<Partial<NotificationSettings> | null>(null);
  const offerRef = useRef<Offer | null>(null);
  offerRef.current = offer;
  const pushSynced = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<DriverMe>('/driver/me');
      setMe(r);
      setLoadError(null);
      settingsRef.current = r.notificationSettings;
      // once per start: this phone's pushes belong to whoever is signed in now
      if (!pushSynced.current) {
        pushSynced.current = true;
        void syncPush(r.pushNative);
      }
      setPresenceState(r.profile.status === 'SUSPENDED' ? 'SUSPENDED' : (r.profile.presence_status ?? 'OFFLINE'));
      setActive(r.activeService);
      if (r.activeService) setResult(null);
      if (r.openOffers?.length && !offerRef.current) showOffer(toOffer(r.openOffers[0]));
    } catch (err) {
      setLoadError(errorText(err));
      toast(errorText(err), true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showOffer = useCallback((o: Offer) => {
    setOffer(o);
    const s = settingsRef.current ?? {};
    const res = startAlert({
      sound: s.sound_enabled ?? true,
      tone: s.sound_key ?? 'classic',
      volume: s.volume ?? 100,
      vibrate: s.vibration_enabled ?? true,
      title: 'Nuevo servicio',
      body: `${o.originText ?? ''} → ${o.destinationText ?? ''} ${o.fareText ?? ''}`,
      tag: `offer-${o.offerId}`,
    });
    setAlertFallback(!res.sound && (s.sound_enabled ?? true));
    socket.send({ type: 'offer.viewed', offerId: o.offerId });
    // Android shell: the same offer also arrives by FCM a moment later; the alert above already covers it
    for (const ms of [1500, 5000]) setTimeout(() => void clearOfferPushes(), ms);
  }, []);

  const clearOffer = useCallback(() => {
    stopAlert();
    setOffer(null);
    setAlertFallback(false);
    void clearOfferPushes();
    setTimeout(() => void clearOfferPushes(), 4000); // the notice that closes the offer comes after
  }, []);

  // socket wiring
  useEffect(() => {
    const offs = [
      socket.onState(setSocketState),
      socket.on('offer.new', (m) => {
        if (!offerRef.current) showOffer(toOffer(m.offer));
      }),
      socket.on('offer.withdrawn', (m) => {
        const o = offerRef.current;
        if (o && o.offerId === m.offerId) {
          clearOffer();
          // it stays on the home screen: the driver may open the app from the notification a moment later
          setResult({ kind: 'missed', code: o.code, fareMinor: null, currency: '' });
          toast('La oferta ya no está disponible');
        }
      }),
      socket.on('service.updated', () => void refresh()),
      socket.on('service.released', () => {
        const s = activeRef.current;
        if (s) setResult({ kind: 'released', code: s.code, fareMinor: null, currency: s.currency });
        toast('El servicio fue liberado o cancelado');
        void refresh();
      }),
      socket.on('presence.changed', (m) => setPresenceState(m.status)),
      socket.on('account.suspended', (m) => {
        toast(`Cuenta suspendida: ${m.reason}`, true);
        void refresh();
      }),
      socket.on('access.changed', () => void refresh()),
      socket.on('notification', (m) => m.notification?.category !== 'OFFER' && toast(m.notification?.title ?? 'Notificación')),
      socket.on('voice.incoming', (m) => {
        const vc = new VoiceCall(m.sessionId, m.iceServers, () => setCall(null));
        setCall({ sessionId: m.sessionId, from: m.from, call: vc, state: 'ringing' });
        startAlert({ sound: true, tone: 'soft', volume: 80, vibrate: true, title: 'Llamada entrante', body: m.from?.name ?? '', tag: m.sessionId });
      }),
      socket.on('voice.ended', () => stopAlert()),
      socket.on('auth.ok', () => void refresh()),
    ];
    const onSw = (e: MessageEvent) => {
      if (e.data?.type === 'push' && e.data.payload?.category === 'OFFER' && !offerRef.current) showOffer(toOffer(e.data.payload.data));
      if (e.data?.type === 'notification-click') void refresh();
    };
    navigator.serviceWorker?.addEventListener('message', onSw);
    // Android shell: a notification was tapped (device.ts)
    const onPushOpened = () => void refresh();
    window.addEventListener('driver:push-opened', onPushOpened);
    socket.connect();
    void refresh();
    return () => {
      offs.forEach((f) => f());
      navigator.serviceWorker?.removeEventListener('message', onSw);
      window.removeEventListener('driver:push-opened', onPushOpened);
      socket.close();
    };
  }, [refresh, showOffer, clearOffer, toast]);

  // push-to-talk radio: loaded on demand (keeps the WebRTC library out of the bundle when disabled)
  const radioEnabled = !!me?.radio;
  const radioSound = me?.notificationSettings?.sound_enabled ?? true;
  const radioVibration = me?.notificationSettings?.vibration_enabled ?? true;
  const radioUser: string | undefined = me?.profile?.user_id;
  useEffect(() => {
    if (!radioEnabled) return;
    let client: RadioClient | null = null;
    let cancelled = false;
    void import('@despachador/api-client/ptt').then(({ RadioClient }) => {
      if (cancelled) return;
      client = new RadioClient({
        socket,
        fetchChannels: () => api.get('/me/ptt/channels'),
        wantMic: true,
        tones: radioSound,
        vibration: radioVibration,
        storageKey: 'driver.radio.channel',
        userId: radioUser,
      });
      client.start();
      setRadio(client);
    });
    return () => {
      cancelled = true;
      client?.stop(); // logout / unmount: the turn is released and the microphone closed
      setRadio(null);
    };
  }, [radioEnabled, radioSound, radioVibration, radioUser]);

  // Android shell: tell the phone while someone else is on the air (audio focus, loudspeaker)
  useEffect(() => {
    if (!radio) return;
    let hearing = false;
    const sync = () => {
      const s = radio.getSnapshot();
      const now = Object.values(s.channels).some((c) => c.speaker && c.speaker.userId !== s.userId && !s.mutedChannels.includes(c.id));
      if (now === hearing) return;
      hearing = now;
      radioAudio(now);
    };
    const off = radio.subscribe(sync);
    sync();
    return () => {
      off();
      if (hearing) radioAudio(false);
    };
  }, [radio]);

  // offer expiry
  useEffect(() => {
    if (!offer) return;
    const ms = new Date(offer.expiresAt).getTime() - Date.now();
    const id = setTimeout(() => {
      clearOffer();
      // the full-screen "se acabó el tiempo" says it (OfferExpired); then the card stays on the home screen
      setResult({ kind: 'missed', timedOut: true, code: offer.code, fareMinor: null, currency: '' });
    }, Math.max(0, ms));
    return () => clearTimeout(id);
  }, [offer, clearOffer, toast]);

  // location + wake lock follow availability
  useEffect(() => {
    const working = presence === 'ONLINE' || presence === 'BUSY' || presence === 'PAUSED';
    if (working) {
      startLocation(setGeo);
      void keepAwake(presence !== 'PAUSED');
    } else {
      stopLocation();
      setGeo('off');
      void keepAwake(false);
    }
    const onVis = () => document.visibilityState === 'visible' && working && void keepAwake(presence !== 'PAUSED');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [presence]);

  const enableAudio = useCallback(async () => {
    setAudioReady(await unlockAudio());
    setAlertFallback(false);
  }, []);

  const setPresence = useCallback(
    async (s: 'ONLINE' | 'PAUSED' | 'OFFLINE') => {
      if (s === 'ONLINE') await enableAudio(); // user gesture: unlock audio right here
      // Android 13+ shows no notification without the system permission: asked when the turn starts
      // (the system only shows its dialog while it has not been answered)
      if (s === 'ONLINE' && isNativeApp) await enablePush(null, me?.pushNative ?? false).catch(() => undefined);
      try {
        const r = await api.put('/driver/presence', { status: s });
        setPresenceState(r.status);
      } catch (err) {
        toast(errorText(err), true);
      }
    },
    [enableAudio, toast, me?.pushNative],
  );

  const respond = useCallback(
    async (accept: boolean) => {
      const o = offerRef.current;
      if (!o) return;
      clearOffer();
      try {
        await api.post(`/driver/offers/${o.offerId}/${accept ? 'accept' : 'reject'}`, {});
        // accepted: no toast, the trip itself takes over the home screen
      } catch (err) {
        const offline = (err as { status?: number }).status === 0;
        toast(accept && !offline ? 'No fue posible tomar el servicio: ya fue asignado a otro conductor o expiró.' : errorText(err), true);
      }
      await refresh();
    },
    [clearOffer, refresh, toast],
  );

  const action = useCallback(
    async (a: string, reason?: string) => {
      if (!active) return false;
      try {
        await api.post(`/driver/services/${active.id}/${a}`, reason ? { reason } : {});
        const kind = ({ complete: 'completed', cancel: 'cancelled', no_show: 'no_show' } as const)[a as 'complete' | 'cancel' | 'no_show'];
        if (kind) setResult({ kind, code: active.code, fareMinor: kind === 'completed' ? active.fare_total_minor : null, currency: active.currency });
        await refresh();
        return true;
      } catch (err) {
        toast(errorText(err), true);
        return false;
      }
    },
    [active, refresh, toast],
  );

  const saveSettings = useCallback(
    async (s: Record<string, unknown>) => {
      const r = await api.put<NotificationSettings>('/me/notification-settings', s);
      settingsRef.current = r;
      setMe((m) => (m ? { ...m, notificationSettings: r } : m));
    },
    [],
  );

  const dismissResult = useCallback(() => setResult(null), []);

  const acceptCall = useCallback(async () => {
    if (!call) return;
    stopAlert();
    try {
      await call.call.accept();
      setCall({ ...call, state: 'active' });
    } catch (err) {
      toast(`No se pudo contestar: ${errorText(err)}`, true);
      await call.call.hangup();
    }
  }, [call, toast]);

  const endCall = useCallback(
    async (reject: boolean) => {
      stopAlert();
      if (!call) return;
      if (reject) await call.call.reject();
      else await call.call.hangup();
      setCall(null);
    },
    [call],
  );

  return (
    <Ctx.Provider
      value={{ me, loading, loadError, presence, socketState, offer, active, result, dismissResult, geo, audioReady, alertFallback, call, radio, refresh, setPresence, respond, action, enableAudio, saveSettings, acceptCall, endCall }}
    >
      {children}
    </Ctx.Provider>
  );
}
